import {useAppDispatch} from '../../../redux/hooks';
import React, {useEffect} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {GetRaces, GetSubraces} from '../../../GraphQL/SystemQueries';
import {
    CreateSubrace,
    CreateSubraceFeature,
    DeleteSubrace, DeleteSubraceFeature,
    UpdateSubrace,
    UpdateSubraceFeature
} from '../../../GraphQL/SystemMutations';
import {toast} from 'react-toastify';
import {setActiveContent, setIsAppLoading} from '../../../redux/slices/content/contentSlice';
import Button from '../../../Atoms/Button';
import {DeleteSharp, PlayArrowSharp} from '@mui/icons-material';
import {PopUp} from '../../../Atoms/PopUp';
import Input from '../../../Atoms/Input';
import {TextEditor} from '../../../Atoms/TextEditor';
import {ActiveContent} from '../../../redux/slices/content/contentType';
import {DropDown} from '../../../Atoms/DropDown';
import {VeneList} from "../../../Atoms/VeneList";
import {Race, RaceFeature, Subrace, SubraceFeature} from "../../../redux/slices/character/characterType";
import {emptyRace} from "./Voelker";
import {DropdownEntry} from "../../../Helper/Constants";
import * as _ from "lodash";

export interface SubraceToEdit extends Subrace{
    Race: {
        raceId: number;
        raceName: string;
    };
}

const emptySubrace: SubraceToEdit = {
    subraceDescription: "",
    subraceFeatures: [],
    subraceId: 0,
    subraceName: "",
    Race: {
        raceId: 0,
        raceName: "",
    }
}


export const Subvoelker = () => {
    const dispatch = useAppDispatch();

    const [ subraces, setSubraces ] = React.useState<SubraceToEdit[]>([]);
    const [ races, setRaces ] = React.useState<DropdownEntry[]>([]);
    const [ selectedSubrace, setSelectedSubrace ] = React.useState<SubraceToEdit>(emptySubrace);
    const [ deleteSubraceId, setDeleteSubraceId ] = React.useState<number>(0)
    const [ isPopUpVisible, setIsPopUpVisible ] = React.useState<boolean>(false);
    const [ isDeletePopUpVisible, setIsDeletePopUpVisible ] = React.useState(false)

    const {data: raceData, loading: raceLoading, error: raceError} = useQuery(GetRaces, {fetchPolicy: 'network-only'});
    const {data: subraceData, loading: subraceLoading, error: subraceError} = useQuery(GetSubraces, {fetchPolicy: 'network-only'});
    const [ createSubraceHook, {}] = useMutation(CreateSubrace);
    const [ updateSubraceHook, {}] = useMutation(UpdateSubrace);
    const [ deleteSubraceHook, {}] = useMutation(DeleteSubrace);

    const [ createSubraceFeatureHook, {}] = useMutation(CreateSubraceFeature);
    const [ updateSubraceFeatureHook, {}] = useMutation(UpdateSubraceFeature);
    const [ deleteSubraceFeatureHook, {}] = useMutation(DeleteSubraceFeature);

    useEffect(() => {
        if (raceData && !raceLoading) {
            const raceList = [];
            for (const race of raceData.getRaces) {
                raceList.push({
                    itemValue: race.raceId,
                    itemText: race.raceName
                })
            }
            setRaces(raceList);
        }
    }, [ raceData ])

    useEffect(() => {
        if (subraceData && !subraceLoading) {
            setSubraces(subraceData.getSubraces);
        }
    }, [ subraceData ])

    useEffect(() => {
        if (subraceError) {
            toast('Fehler beim Laden der Völker')
        }
    }, [ subraceError ])

    useEffect(() => {
        if (subraceLoading) {
            dispatch(setIsAppLoading({isAppLoading: true, loadingText: 'Völker werden geladen...'}))
        } else {
            dispatch(setIsAppLoading({isAppLoading: false, loadingText: ''}))
        }
    }, [ subraceLoading ])

    const editSubrace = (subrace: SubraceToEdit) => {
        setSelectedSubrace({
            subraceId: subrace.subraceId,
            subraceName: subrace.subraceName,
            subraceDescription: subrace.subraceDescription,
            subraceFeatures: subrace.subraceFeatures,
            Race: {
                raceId: subrace.Race.raceId,
                raceName: subrace.Race.raceName
            }
        });
        setIsPopUpVisible(true)
    }

    const deleteSubrace = (subrace: any) => {
        setDeleteSubraceId(subrace.subraceId);
        setIsDeletePopUpVisible(true);
    }

    const handleUpdateSubrace = async () => {
        if (selectedSubrace) {
            const updateData = await updateSubraceHook({
                variables: {
                    subraceId: Number(selectedSubrace.subraceId),
                    subraceName: selectedSubrace.subraceName,
                    subraceDescription: selectedSubrace.subraceDescription,
                    raceId: Number(selectedSubrace.Race.raceId)
                },
                refetchQueries: [{
                    query: GetSubraces
                }]
            })

            if (!updateData.errors) {
                toast(`Das Subvolk: ${selectedSubrace.subraceName} wurde gespeichert!`);
                setSelectedSubrace(emptySubrace);
                setIsPopUpVisible(false);
            } else {
                toast(`Fehler beim Speichern des Volks: ${selectedSubrace.subraceName}!`)
            }
        }
    }

    const handleCreateSubrace = async () => {
        if (selectedSubrace) {
            const createData = await createSubraceHook({
                variables: {
                    subraceName: selectedSubrace.subraceName,
                    subraceDescription: selectedSubrace.subraceDescription,
                    raceId: Number(selectedSubrace.Race.raceId)
                },
                refetchQueries: [{
                    query: GetSubraces
                }]
            })

            if (!createData.errors) {
                toast(`Das Subvolk: ${selectedSubrace.subraceName} wurde erstellt!`);
                setSelectedSubrace(emptySubrace);
                setIsPopUpVisible(false);
            } else {
                toast(`Fehler beim erstellen des Volks: ${selectedSubrace.subraceName}!`)
            }
        }
    }

    const handleDeleteSubrace = async () => {
        const deleteData = await deleteSubraceHook({
            variables: {
                subraceId: Number(deleteSubraceId),
            },
            refetchQueries: [{
                query: GetSubraces
            }]
        })

        if (!deleteData.errors) {
            toast(`Das Subvolk wurde erfolgreich gelöscht!`)
        } else {
            toast(`Fehler beim Löschen des Volkes!`)
        }

        setDeleteSubraceId(0);
        setIsDeletePopUpVisible(false);
    }

    const handleClosePopUp = () => {
        setSelectedSubrace(emptySubrace);
        setIsPopUpVisible(false)
    }

    const deleteSubraceFeature = async (subraceFeatureId: number) => {
        await deleteSubraceFeatureHook({
            variables: {
                subraceFeatureId: Number(subraceFeatureId)
            }
        })
    }

    const saveSubraceFeature = async (subraceFeature: SubraceFeature) => {
        if (subraceFeature.subraceFeatureId > 0) {
            await updateSubraceFeatureHook({
                variables: {
                    subraceFeatureId: Number(subraceFeature.subraceFeatureId),
                    subraceFeatureName: subraceFeature.subraceFeatureName,
                    subraceFeatureDesc: subraceFeature.subraceFeatureDesc
                },
                refetchQueries: [{
                    query: GetSubraces
                }]
            })
        } else {
            const response = await createSubraceFeatureHook({
                variables: {
                    subraceId: Number(selectedSubrace.subraceId),
                    subraceFeatureName: subraceFeature.subraceFeatureName,
                    subraceFeatureDesc: subraceFeature.subraceFeatureDesc
                },
                refetchQueries: [{
                    query: GetSubraces
                }]
            })

            if (!response.errors) {
                toast(`Feature gespeichert`);
                setSelectedSubrace(response.data.createSubraceFeature)
            }
        }
    }

    const renderOneRaceFeature = (subraceFeature: SubraceFeature, index: number) => {
        return (
            <div className='abilityContainer mt-3'>
                <div className='row justify-content-end'>
                    <div className='col-2 text-end'>
                        <Button onClick={ async () => await deleteSubraceFeature(subraceFeature.subraceFeatureId)}>
                            Löschen
                        </Button>
                    </div>
                    <div className='col-2 text-end'>
                        <Button onClick={ async() => await saveSubraceFeature(subraceFeature)}>
                            Speichern
                        </Button>
                    </div>
                </div>
                <Input label="Name" type="text" value={subraceFeature.subraceFeatureName} onChange={(e: any) => {
                    let updatedSubraceFeature = _.cloneDeep(selectedSubrace.subraceFeatures);
                    updatedSubraceFeature[index].subraceFeatureName = e.target.value;
                    setSelectedSubrace({
                        ...selectedSubrace,
                        subraceFeatures: updatedSubraceFeature
                    });
                }}/>
                <TextEditor label="Beschreibung" onChange={ (e) => {
                    let updatedSubraceFeature = _.cloneDeep(selectedSubrace.subraceFeatures);
                    if (e !== updatedSubraceFeature[index].subraceFeatureDesc) {
                        updatedSubraceFeature[index].subraceFeatureDesc = e;
                        setSelectedSubrace({
                            ...selectedSubrace,
                            subraceFeatures: updatedSubraceFeature
                        });
                    }
                }} value={subraceFeature.subraceFeatureDesc}/>
            </div>
        )
    }

    const renderAllRaceFeatures = () => {
        const subraceFeatureHMTL = [];
        let index = 0;
        if (selectedSubrace) {
            for (const subraceFeature of selectedSubrace.subraceFeatures) {
                subraceFeatureHMTL.push(renderOneRaceFeature(subraceFeature, index));
                index++
            }
        }

        return subraceFeatureHMTL
    }

    const addNewSubRaceFeature = () => {
        let updatedSubraceFeatures = _.cloneDeep(selectedSubrace.subraceFeatures);
        updatedSubraceFeatures.push({
            subraceFeatureId: 0,
            subraceFeatureDesc: "",
            subraceFeatureName: ""
        });
        setSelectedSubrace({
            ...selectedSubrace,
            subraceFeatures: updatedSubraceFeatures
        })
    }

    return (
        <div>
            {(selectedSubrace.subraceId !== 0 && isPopUpVisible) && (
                <PopUp onClose={handleClosePopUp} title='Subvolk bearbeiten'>
                    <div className='row justify-content-end'>
                        <div className='col-2'>
                            <Button onClick={ async () => selectedSubrace.subraceId > 0? await handleUpdateSubrace() : await handleCreateSubrace() }>{selectedSubrace.subraceId > 0 ? 'Speichern' : 'Erstellen'}</Button>
                        </div>
                    </div>
                    <div className='row mt-3'>
                        <div className='col-1'>
                            <label>Subvolk:</label>
                        </div>
                        <div className='col-11'>
                            <DropDown value={selectedSubrace.Race.raceId} setValue={(event) => {
                                setSelectedSubrace({
                                    subraceId: selectedSubrace.subraceId,
                                    subraceName: selectedSubrace.subraceName,
                                    subraceDescription: selectedSubrace.subraceDescription,
                                    subraceFeatures: selectedSubrace.subraceFeatures,
                                    Race: {
                                        raceId: Number(event.target.value),
                                        raceName: event.target.name
                                    }
                                })
                            }} items={races}/>
                        </div>
                        <div className='col-1'>
                            <label>Name:</label>
                        </div>
                        <div className='col-11'>
                            <Input value={selectedSubrace.subraceName} onChange={(e) => setSelectedSubrace({
                                subraceId: selectedSubrace.subraceId,
                                subraceName: e.target.value,
                                subraceDescription: selectedSubrace.subraceDescription,
                                subraceFeatures: selectedSubrace.subraceFeatures,
                                Race: {
                                    raceId: selectedSubrace.Race.raceId,
                                    raceName: selectedSubrace.Race.raceName
                                }
                            })}/>
                        </div>
                        <div className='col-1'>
                            <label>Beschreibung:</label>
                        </div>
                        <div className='col-11'>
                            <TextEditor value={selectedSubrace.subraceDescription} onChange={(value) => setSelectedSubrace({
                                subraceId: selectedSubrace.subraceId,
                                subraceName: selectedSubrace.subraceName,
                                subraceDescription: value,
                                subraceFeatures: selectedSubrace.subraceFeatures,
                                Race: {
                                    raceId: selectedSubrace.Race.raceId,
                                    raceName: selectedSubrace.Race.raceName
                                }
                            })}/>
                        </div>
                    </div>
                    { selectedSubrace.subraceId > 0 &&
                        <div>
                            <Button onClick={ () => addNewSubRaceFeature() }>+ Feature</Button>
                            { renderAllRaceFeatures() }
                        </div>
                    }
                </PopUp>
            )}
            {(selectedSubrace.subraceId === 0 && !isPopUpVisible) && (
                <div>
                    { isDeletePopUpVisible && (
                        <PopUp onClose={setIsDeletePopUpVisible} title='Subvolk löschen'>
                            <div className='row mt-3 justify-content-center'>
                                <div className='col-12'>
                                    <b>Sind sie sicher, dass sie das Subvolk löschen möchten?</b>
                                </div>
                                <div className='col-2'>
                                    <Button onClick={ async() => await handleDeleteSubrace() }>Löschen</Button>
                                </div>
                                <div className='col-2'>
                                    <Button onClick={() => {
                                        setDeleteSubraceId(0);
                                        setIsDeletePopUpVisible(false)
                                    }}>Abbrechen</Button>
                                </div>
                            </div>
                        </PopUp>
                    )}
                    <div className='row justify-content-between'>
                        <div className='col-2'>
                            <Button onClick={() => dispatch(setActiveContent(ActiveContent.Standardinhalte))}>
                                Zurück
                            </Button>
                        </div>
                        <div className='col-2'>
                            <Button onClick={() => {
                                setSelectedSubrace({
                                    subraceId: -1,
                                    subraceName: '',
                                    subraceDescription: '',
                                    subraceFeatures: [],
                                    Race: {
                                        raceId: 0,
                                        raceName: ''
                                    }
                                })
                                setIsPopUpVisible(true)
                            }}>
                                Hinzufügen
                            </Button>
                        </div>
                    </div>
                    <VeneList<SubraceToEdit>
                        data={subraces}
                        colDefinition={[
                            {
                                keyName: "subraceId",
                                colName: "ID"
                            },
                            {
                                keyName: "subraceName",
                                colName: "Name"
                            }
                        ]}
                        buttonConfig={[
                            {
                                icon: <PlayArrowSharp/>,
                                onClick: (dataEntry) => { editSubrace(dataEntry) }
                            },
                            {
                                icon: <DeleteSharp/>,
                                onClick: (dataEntry) => { deleteSubrace(dataEntry) }
                            }
                        ]}
                    />
                </div>
            )}
        </div>
    )
}